/**
 * Map-backed `PluginRegistry` implementation + the process-wide singleton.
 *
 * Storage is one Map per kind. Registration rejects duplicate ids
 * (`RegistrationError`) and any target not on `apiVersion: 1`
 * (`ApiVersionMismatchError`).
 */

import { ApiVersionMismatchError, RegistrationError } from "./errors.js";
import type { PluginRegistry, Target, TargetKind, TargetMeta } from "./registry.js";

type AnyTarget = { id: string; displayName: string; apiVersion: unknown };

class MapPluginRegistry implements PluginRegistry {
  readonly apiVersion = 1 as const;

  // One Map per kind keeps lookups kind-scoped — a driver and a connector may
  // share an id without colliding.
  private readonly stores: Record<TargetKind, Map<string, AnyTarget>> = {
    driver: new Map(),
    connector: new Map(),
    deployTarget: new Map(),
  };

  register<K extends TargetKind>(kind: K, target: Target<K>): void {
    const t = target as unknown as AnyTarget;
    if (t.apiVersion !== 1) {
      throw new ApiVersionMismatchError(kind, t.id, t.apiVersion);
    }
    const store = this.stores[kind];
    if (store.has(t.id)) {
      throw new RegistrationError(kind, t.id);
    }
    store.set(t.id, t);
  }

  unregister<K extends TargetKind>(kind: K, id: string): boolean {
    return this.stores[kind].delete(id);
  }

  get<K extends TargetKind>(kind: K, id: string): Target<K> | undefined {
    return this.stores[kind].get(id) as Target<K> | undefined;
  }

  list<K extends TargetKind>(kind: K): readonly TargetMeta[] {
    return [...this.stores[kind].values()].map((t) => ({
      id: t.id,
      displayName: t.displayName,
    }));
  }
}

/** Create a fresh, isolated registry — used by tests so cases never share state. */
export function createPluginRegistry(): PluginRegistry {
  return new MapPluginRegistry();
}

/** Process-wide default registry. Built-ins and loaded plugins register here. */
export const pluginRegistry: PluginRegistry = createPluginRegistry();
